import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { throttle } from 'lodash';
import slides from '../../assets/data/slides.json';

import homer168 from '../../assets/images/homer168.jpg';
import homer291 from '../../assets/images/homer291.jpg';
import homer500 from '../../assets/images/homer500.jpg';
import homer541 from '../../assets/images/homer541.jpg';
import homer1004 from '../../assets/images/homer1004.jpg';
import homer1015 from '../../assets/images/homer1015.jpg';

const homerImages = [
  {
    slide: 4,
    image: homer1004,
  },
  {
    slide: 5,
    image: homer168,
  },
  {
    slide: 7,
    image: homer291,
  },
  {
    slide: 8,
    image: homer1015,
  },
  {
    slide: 10,
    image: homer500,
  },
  {
    slide: 11,
    image: homer541,
  },
];

const Wrapper = styled.div`
  position: relative;
  z-index: 25;
  -webkit-transform: translate3d(0,0,0);
`;

const WrapperPadder = styled.div`
  width: 100%;
  height: 150vh;
  z-index: 0;
  display: block;
`;

const Slide = styled.div`
  height: 125vh;
  width: 100%;
  position: relative;
  &:last-of-type {
    height: auto;
  }
  @media (max-width: 900px) {
    height: 150vh;
  }
`;

const SlideContent = styled.div`
  max-width: 350px;
  width: 95%;
  padding: 1rem;
  background-color: white;
  border: 1px solid #d42d24;
  position: absolute;
  left: 0;
  transform: translate(0, 0%);
  @media(max-width: 900px) {
    left: 50%;
    transform: translate(-50%, 0%);
  }
  p {
    font-size: 0.75rem;
    line-height: 1rem;
    text-align: left;
  }
`;

const HrOverline = styled.h6`
  padding-bottom: 0.5rem;
  font-size: 1.5rem;
  line-height: 1.5rem;
  margin: 0 auto;
  display: block;
`;

const HrDate = styled.p`
  text-align: center;
  color: rgb(66,66,66);
  margin-bottom: 0.75rem;
  border-bottom: 1px solid rgb(121,121,121);
  display: inline-block;
  line-height: 0.875rem;
`;

const SlidePhotoCredit = styled.p`
  text-align: right !important;
  font-size: 0.75rem;
  width: 100%;
  display: block;
  margin: 0 0 0.25rem;
  text-transform: uppercase;
`;

const Scrollytelling = ({
  setNumberFilter,
  setVenueFilter,
  setFiltersDisable,
  setFilterDisplay,
  clearFilters,
}) => {
  const currentSlide = useRef(-1);
  const scrollytellingRef = useRef(null);

  useEffect(() => {
    const slideElements = document.querySelectorAll('.scrollytelling__slide');

    // determines the filter on scroll
    function findFilterOnScroll() {
      // updates the currentSlide ref with the index of the last slide
      // to pass the halfway point of the window
      currentSlide.current = -1;
      slideElements.forEach((slide, i) => {
        const slideTop = slide.getBoundingClientRect().top;
        if (slideTop < window.innerHeight) {
          currentSlide.current = i;
        }
      });

      // if we're into the scrollytelling and we're not on the last slide,
      // clear filters and hide filter display on scroll
      if (currentSlide.current !== -1 && currentSlide.current !== slideElements.length - 1) {
        clearFilters();
        setFilterDisplay(false);
      }

      // if the currentSlide is defined, grab the hr number from the slide element
      // else, if no slide has been reached yet, set the hrFilter to -1
      if (currentSlide.current !== -1) {
        const hrNumberData = slideElements[currentSlide.current].getAttribute('data-hrnumber').split(',');
        const venue = slideElements[currentSlide.current].getAttribute('data-venue');
        const hrNumber = [];
        if (hrNumberData.length > 0 && hrNumberData[0] !== '-1') {
          hrNumberData.forEach((num) => hrNumber.push(parseInt(num, 10)));
        }
        setNumberFilter(hrNumber);
        setVenueFilter(venue);
      } else if (currentSlide.current === undefined || currentSlide.current === -1) {
        setNumberFilter([]);
        setVenueFilter('all');
      }

      // if we're on the last slide and it's scrolled out of view, enable the filters
      // else disable them
      if (currentSlide.current === slideElements.length - 1
        // eslint-disable-next-line
        && slideElements[currentSlide.current].getBoundingClientRect().bottom < window.innerHeight * 0.1) {
        setFiltersDisable(false);
        setFilterDisplay(true);
      } else {
        setFiltersDisable(true);
        setFilterDisplay(false);
      }
    }

    // adds the scroll event listener when the scrollytelling component is mounted
    if (scrollytellingRef.current) {
      window.addEventListener('scroll', throttle(findFilterOnScroll, 100));
    }

    // cleans up the event listener
    return _ => {
      window.removeEventListener('scroll', findFilterOnScroll);
    };
  }, [
    setNumberFilter,
    setVenueFilter,
    setFiltersDisable,
    clearFilters,
    setFilterDisplay,
    scrollytellingRef,
  ]);

  const slideMarkup = slides.map((slide) => {
    let slideImage = null;
    const slideHasImage = homerImages.find((obj) => obj.slide === slide.slide);
    if (slideHasImage !== undefined) {
      slideImage = slideHasImage.image;
    }

    return (
      <Slide
        className="scrollytelling__slide"
        key={`ortiz-scrollytelling-slide-${slide.slide}`}
        data-hrnumber={slide.filters.hrNumber}
        data-venue={slide.filters.venue}
      >
        <SlideContent className="slide__content">
          {slideImage
            && (
              <div className="slide__image-container">
                <img src={slideImage} alt={slide.alt} />
                <SlidePhotoCredit className="slide__photo-credit bentonsanscond-n4">{slide.credit}</SlidePhotoCredit>
              </div>
            )}
          {slide.date !== undefined && <HrDate className="bentonsanscond-n6">{slide.date}</HrDate>}
          {slide.hrNumber !== undefined && <HrOverline className="benton-n6">{slide.hrNumber}</HrOverline>}
          {slide.series !== undefined && <HrOverline className="benton-n6">{slide.series}</HrOverline>}
          <p className="benton-n4">{slide.text}</p>
        </SlideContent>
      </Slide>
    );
  });

  return (
    <React.Fragment>
      <Wrapper id="scrollytelling__container" ref={scrollytellingRef}>
        {slideMarkup}
      </Wrapper>
      <WrapperPadder id="scrollytelling__padding" />
    </React.Fragment>
  );
};

export default Scrollytelling;

Scrollytelling.propTypes = {
  setNumberFilter: PropTypes.func.isRequired,
  setVenueFilter: PropTypes.func.isRequired,
  setFiltersDisable: PropTypes.func.isRequired,
  setFilterDisplay: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
};
